/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

import PropTypes from "prop-types";
import { Box, Card, Chip, Divider } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import StatusBadge from "components/StatusBadge";
import EntityStatusChip from "./EntityStatusChip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

const EmptyState = ({ message }) => (
    <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={3}
        px={2}
        textAlign="center"
    >
        <MDTypography variant="body2" color="text">
            {message}
        </MDTypography>
    </MDBox>
);

EmptyState.propTypes = {
    message: PropTypes.string.isRequired,
};

const formatSecondary = (item, section) => {
    if (typeof section.getSecondary === "function") {
        return section.getSecondary(item);
    }
    if (!section.secondaryField) return null;
    const value = item?.[section.secondaryField];
    if (value == null || value === "") return null;
    if (section.secondaryFormat === "date") {
        try {
            return new Date(value).toLocaleDateString();
        } catch {
            return String(value);
        }
    }
    return String(value);
};

/**
 * One related list on a detail page. Items come from `section.getItems(record)`
 * or, by default, `record.related[section.key]` (the Travel detail payloads).
 * "Add" either navigates (`getAddPath(record)`) or, with `addForm`, asks the
 * detail page to open the generic add dialog (`onAdd(section)`).
 */
const RelatedRecordsSection = ({ section, items, record, navigate, onAdd, canAdd: canAddAny }) => {
    const records = Array.isArray(items) ? items : [];
    const addPath =
        section.allowAdd && typeof section.getAddPath === "function" ? section.getAddPath(record) : null;
    const canAdd = Boolean(canAddAny && section.allowAdd && (addPath || (section.addForm && onAdd)));
    const Icon = section.icon || null;

    const handleAdd = () => {
        if (!canAdd) return;
        if (addPath) {
            navigate(addPath);
            return;
        }
        onAdd(section);
    };

    const handleOpen = (item) => {
        if (!item?.id || !section.getDetailPath) return;
        navigate(section.getDetailPath(item.id), {
            state: { name: item.title || item.name || item.question },
        });
    };

    return (
        <Card sx={{ mb: 3 }}>
            <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
            >
                <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                    {Icon ? <Icon sx={{ color: "white !important", mr: 1, flexShrink: 0 }} /> : null}
                    <MDTypography variant="h6" color="white" noWrap>
                        {section.title} ({records.length})
                    </MDTypography>
                </Box>
                {canAdd ? (
                    <MDButton
                        variant="gradient"
                        color="dark"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                    >
                        Add
                    </MDButton>
                ) : null}
            </MDBox>

            <MDBox p={2}>
                {records.length === 0 ? (
                    <EmptyState message={section.emptyMessage || "No related records."} />
                ) : (
                    records.map((item, index) => {
                        const primary =
                            item?.[section.primaryField] ||
                            item?.title ||
                            item?.name ||
                            `#${item.id}`;
                        const secondary = formatSecondary(item, section);
                        const chipLabel =
                            typeof section.getChipLabel === "function"
                                ? section.getChipLabel(item)
                                : null;

                        return (
                            <Box key={item.id || `${section.key}-${index}`}>
                                {index > 0 && <Divider sx={{ my: 1.5 }} />}
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                    gap={1}
                                    sx={{
                                        cursor: section.getDetailPath ? "pointer" : "default",
                                        borderRadius: 1.5,
                                        p: 1,
                                        mx: -1,
                                        "&:hover": section.getDetailPath
                                            ? { bgcolor: "rgba(23, 193, 232, 0.06)" }
                                            : undefined,
                                    }}
                                    onClick={() => handleOpen(item)}
                                >
                                    <MDBox minWidth={0} flex={1}>
                                        <MDTypography
                                            variant="button"
                                            fontWeight="medium"
                                            color="dark"
                                            display="block"
                                            sx={{
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                                overflowWrap: "anywhere",
                                                lineHeight: 1.35,
                                            }}
                                        >
                                            {primary}
                                        </MDTypography>
                                        {(secondary || chipLabel) && (
                                            <MDBox
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                                mt={0.5}
                                                flexWrap="wrap"
                                            >
                                                {secondary ? (
                                                    <MDTypography variant="caption" color="text">
                                                        {secondary}
                                                    </MDTypography>
                                                ) : null}
                                                {chipLabel ? (
                                                    <Chip
                                                        label={chipLabel}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            height: 20,
                                                            textTransform: "capitalize",
                                                            fontSize: "0.65rem",
                                                        }}
                                                    />
                                                ) : null}
                                            </MDBox>
                                        )}
                                        {item.updated_at && section.showUpdated !== false ? (
                                            <MDTypography
                                                variant="caption"
                                                color="text"
                                                sx={{
                                                    mt: 0.5,
                                                    display: "block",
                                                    fontSize: "0.7rem",
                                                    fontFamily: "monospace",
                                                }}
                                            >
                                                Updated {new Date(item.updated_at).toLocaleDateString()}
                                            </MDTypography>
                                        ) : null}
                                    </MDBox>
                                    {section.showStatus !== false && item.status ? (
                                        section.statusVariants ? (
                                            <EntityStatusChip
                                                status={item.status}
                                                variants={section.statusVariants}
                                                size="xs"
                                            />
                                        ) : (
                                            <StatusBadge status={item.status} />
                                        )
                                    ) : null}
                                    {section.showPublished && item.is_published != null ? (
                                        <Chip
                                            label={item.is_published ? "Published" : "Draft"}
                                            size="small"
                                            color={item.is_published ? "success" : "default"}
                                            sx={{ height: 22, fontSize: "0.65rem" }}
                                        />
                                    ) : null}
                                </MDBox>
                            </Box>
                        );
                    })
                )}
            </MDBox>
        </Card>
    );
};

RelatedRecordsSection.propTypes = {
    section: PropTypes.shape({
        key: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        emptyMessage: PropTypes.string,
        primaryField: PropTypes.string,
        secondaryField: PropTypes.string,
        secondaryFormat: PropTypes.string,
        getSecondary: PropTypes.func,
        getChipLabel: PropTypes.func,
        getDetailPath: PropTypes.func,
        getItems: PropTypes.func,
        allowAdd: PropTypes.bool,
        getAddPath: PropTypes.func,
        addForm: PropTypes.object,
        showStatus: PropTypes.bool,
        showUpdated: PropTypes.bool,
        showPublished: PropTypes.bool,
        statusVariants: PropTypes.object,
        icon: PropTypes.elementType,
    }).isRequired,
    items: PropTypes.arrayOf(PropTypes.object),
    record: PropTypes.object,
    navigate: PropTypes.func.isRequired,
    onAdd: PropTypes.func,
    canAdd: PropTypes.bool,
};

RelatedRecordsSection.defaultProps = {
    items: [],
    record: null,
    onAdd: null,
    canAdd: true,
};

export const getRelatedItems = (section, record) => {
    if (typeof section.getItems === "function") return section.getItems(record) || [];
    return record?.related?.[section.key] || [];
};

const RelatedRecordsPanel = ({ sections, record, navigate, onAdd, canAdd }) => {
    if (!sections?.length) return null;

    return (
        <MDBox>
            {sections.map((section, index) => (
                <MDBox key={section.key} pt={index === 0 ? 0 : 3}>
                    <RelatedRecordsSection
                        section={section}
                        items={getRelatedItems(section, record)}
                        record={record}
                        navigate={navigate}
                        onAdd={onAdd}
                        canAdd={canAdd}
                    />
                </MDBox>
            ))}
        </MDBox>
    );
};

RelatedRecordsPanel.propTypes = {
    sections: PropTypes.arrayOf(PropTypes.object),
    record: PropTypes.object,
    navigate: PropTypes.func.isRequired,
    onAdd: PropTypes.func,
    canAdd: PropTypes.bool,
};

RelatedRecordsPanel.defaultProps = {
    sections: [],
    record: null,
    onAdd: null,
    canAdd: true,
};

export default RelatedRecordsPanel;
